import { useEffect, useMemo, useState } from 'react';
import { useMemoizedFn } from 'ahooks';
import { useNavigate } from 'react-router';
import toast from 'react-hot-toast';
import { isSufeExamClient } from '@/utils/examClient';
import { logError } from '@/utils/logger/arms-config';
import i18n from '@/locales/init';
import { getExamInfo } from '@/api/start';
import store from '@/stores';
import { examSlice } from '@/stores/slice/exam';
import { finishExamSubmission, getExamSubmissionState } from './examSubmit';
import type { MockExamCourse } from './examSubmit';

interface Options {
  recordId: number;
  course: MockExamCourse;
  ready: boolean;
  expired?: boolean;
  // Pending-submission recovery uses the already frozen server draft.
  save?: (signal: AbortSignal) => Promise<unknown>;
  submit: (signal: AbortSignal) => Promise<unknown>;
}

export function useExamSubmission({recordId, course, ready, expired = false, save, submit}: Options) {
  const navigate = useNavigate();
  const [showLoading, setShowLoading] = useState(false);
  const scope = useMemo(() => ({
    controller: new AbortController(),
    task: null as Promise<boolean> | null,
    finalizing: false,
    completed: false,
    lastFailure: '',
    attentionTimer: undefined as ReturnType<typeof setTimeout> | undefined,
    noticeId: `mock-exam-submit-${course}-${recordId}`,
  }), [recordId, course]);

  useEffect(() => {
    const controller = new AbortController();
    scope.controller = controller;
    setShowLoading(false);
    return () => {
      controller.abort();
      clearTimeout(scope.attentionTimer);
      toast.dismiss(scope.noticeId);
    };
  }, [scope]);

  const isFinalizing = useMemoizedFn(() => scope.finalizing || expired);
  const submitHandle = useMemoizedFn((callback: () => void) => {
    if (!ready || scope.controller.signal.aborted) return Promise.resolve(false);
    if (scope.task) return scope.task;
    if (scope.completed) return Promise.resolve(true);

    const signal = scope.controller.signal;
    scope.finalizing = true;
    if (isSufeExamClient() && scope.attentionTimer === undefined) {
      scope.attentionTimer = setTimeout(() => {
        if (!signal.aborted && !scope.completed) {
          toast.error(i18n.t('common.dauyan_exam_submit_attention'), {id: scope.noticeId, duration: Infinity});
        }
      }, 60_000);
    }
    setShowLoading(true);
    const task = finishExamSubmission({
      signal,
      onFailure: failure => {
        const signature = JSON.stringify(failure);
        if (signature === scope.lastFailure) return;
        scope.lastFailure = signature;
        logError(new Error(JSON.stringify({event: 'mock_exam_submission_failed', recordId, course, ...failure})));
      },
      readState: async requestSignal => {
        const response = await getExamInfo({record_id: recordId}, {signal: requestSignal});
        if (response.code !== 200) throw response;
        return getExamSubmissionState(response.data, recordId, course);
      },
      save,
      submit,
    }).then(succeeded => {
      if (signal.aborted) return false;
      if (succeeded) {
        scope.completed = true;
        clearTimeout(scope.attentionTimer);
        toast.dismiss(scope.noticeId);
        if (course === 'writing') {
          store.dispatch(examSlice.actions.writingSubmissionConfirmed(recordId));
        }
        callback();
        navigate(`/mockstart/${recordId}`, {replace: true});
      }
      return succeeded;
    }).finally(() => {
      scope.task = null;
      if (!signal.aborted) {
        setShowLoading(false);
        if (!scope.completed && !isSufeExamClient()) scope.finalizing = false;
      }
    });
    scope.task = task;
    return task;
  });

  return {showLoading, submitHandle, isFinalizing};
}
